// Worker-pool image processing for bulk photo upload.
//
// Decodes + bakes EXIF orientation + resizes photos OFF the main thread, in
// parallel across CPU cores, so importing 70+ photos doesn't lock the UI or
// starve the parallel uploaders. Each photo is a single pass (createImageBitmap
// + OffscreenCanvas) instead of the old two main-thread canvas passes
// (normalizeJpegOrientation + resize).
//
// `processForUpload` returns the processed JPEG Blob, or `null` when the worker
// pipeline is unavailable or fails — callers then use their existing
// main-thread path (normalizeJpegOrientation + resize) so behaviour is never
// worse than before. Aspect ratio is always preserved (longest edge capped).

// Up to (cores - 1), capped at 6. Each worker holds a decoded bitmap +
// OffscreenCanvas, so an unbounded pool can spike memory enough to get a mobile
// tab killed — but the old hard cap of 3 left Android (more HEIC, slower CPU)
// serializing behind too few workers. 6 is the safe ceiling: it keeps low-end
// phones (typically 4-8 logical cores → 3-6 workers) parallel without OOM,
// while desktop/iPhone get the wider pool. Bounded by hardwareConcurrency-1 in
// the constructor so a 4-core phone still spawns at most 3.
const MAX_WORKERS = 6;
// A resize should take well under a second, but under a 60+ photo bulk import
// the pool (≈3-6 workers) is saturated and queued jobs legitimately wait their
// turn. 8s was too tight for that case (a regression): every queued job blew
// past it and timed out → the whole batch fell to `null` → no thumbnails / no
// dims. 20s restores the pre-regression headroom; a genuinely wedged worker is
// now ALSO recycled on timeout (see run()) so a stuck job can't shrink the pool.
const JOB_TIMEOUT_MS = 20_000;

export const workerPipelineSupported =
  typeof Worker !== 'undefined' &&
  typeof OffscreenCanvas !== 'undefined' &&
  typeof createImageBitmap !== 'undefined';

/** Processed blob plus the EXIF-baked DECODED source dims (upright). */
export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
}

interface Resolver {
  resolve: (r: ProcessedImage) => void;
  reject: (e: Error) => void;
}

class WorkerPool {
  private workers: Worker[] = [];
  private idle: Worker[] = [];
  private queue: Array<{ blob: Blob; maxDim: number; quality: number; r: Resolver }> = [];
  private pending = new Map<Worker, Resolver>();
  private seq = 0;
  private readonly size: number;

  constructor() {
    const hw = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
    this.size = Math.max(1, Math.min(MAX_WORKERS, hw - 1));
  }

  private spawn(): Worker {
    const w = new Worker(new URL('./imageResize.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent) => {
      const r = this.pending.get(w);
      this.pending.delete(w);
      this.idle.push(w);
      if (r) {
        if (e.data?.error) r.reject(new Error(e.data.error));
        else r.resolve({
          blob: e.data.blob as Blob,
          width: typeof e.data.srcWidth === 'number' ? e.data.srcWidth : 0,
          height: typeof e.data.srcHeight === 'number' ? e.data.srcHeight : 0,
        });
      }
      this.drain();
    };
    w.onerror = () => {
      const r = this.pending.get(w);
      this.pending.delete(w);
      // Drop the broken worker from rotation; don't re-queue it.
      this.workers = this.workers.filter((x) => x !== w);
      if (r) r.reject(new Error('worker-error'));
      this.drain();
    };
    return w;
  }

  // Terminate a wedged/broken worker and remove it from every pool list, then
  // top the pool back up to full size so capacity is restored (a timed-out job
  // must NOT permanently shrink the pool → that was the cascade root cause).
  private recycle(w: Worker): void {
    try { w.terminate(); } catch { /* noop */ }
    this.pending.delete(w);
    this.workers = this.workers.filter((x) => x !== w);
    this.idle = this.idle.filter((x) => x !== w);
    // Respawn a fresh idle worker to keep real capacity at full size.
    if (this.workers.length < this.size) {
      const fresh = this.spawn();
      this.workers.push(fresh);
      this.idle.push(fresh);
    }
    this.drain();
  }

  private drain(): void {
    while (this.queue.length > 0 && this.idle.length > 0) {
      const job = this.queue.shift()!;
      const w = this.idle.pop()!;
      this.pending.set(w, job.r);
      w.postMessage({ id: ++this.seq, blob: job.blob, maxDim: job.maxDim, quality: job.quality });
    }
  }

  // Returns the result promise plus a `cancel()` the caller invokes on its own
  // timeout. cancel() recycles the worker IF this job is still the one wedged in
  // it (so a job that finished a hair after the timeout doesn't nuke a worker
  // that has already moved on to someone else's job).
  run(blob: Blob, maxDim: number, quality: number): { promise: Promise<ProcessedImage>; cancel: () => void } {
    let assigned: Worker | null = null;
    const r: Resolver = { resolve: () => {}, reject: () => {} };
    const promise = new Promise<ProcessedImage>((resolve, reject) => {
      r.resolve = resolve;
      r.reject = reject;
    });
    // Lazily grow the pool up to its size cap.
    if (this.workers.length < this.size && this.idle.length === 0) {
      const w = this.spawn();
      this.workers.push(w);
      this.idle.push(w);
    }
    this.queue.push({ blob, maxDim, quality, r });
    // Track which worker actually picked this job up so cancel() recycles the
    // right one. We wrap drain by snapshotting pending before/after isn't
    // reliable; instead resolve `assigned` lazily inside cancel from `pending`.
    this.drain();
    const cancel = () => {
      // Find the worker currently holding THIS job's resolver and recycle it.
      for (const [w, res] of this.pending) {
        if (res === r) { assigned = w; break; }
      }
      if (assigned) this.recycle(assigned);
      // If still queued (never assigned), drop it from the queue so it can't run
      // on a stale blob after the caller already fell back to the main thread.
      this.queue = this.queue.filter((j) => j.r !== r);
    };
    return { promise, cancel };
  }
}

let pool: WorkerPool | null = null;

/**
 * Decode + bake EXIF orientation + resize a photo to print spec, off the main
 * thread. Returns the processed JPEG Blob PLUS the EXIF-baked source dims, or
 * `null` when the worker pipeline is unavailable/slow/failed (caller should
 * fall back to its main-thread path). On a per-job timeout the wedged worker is
 * RECYCLED (terminate + respawn) so a stuck job can't shrink the pool and
 * cascade the whole batch into the null fallback. Never enlarges; longest edge
 * capped at `maxDim`, aspect ratio preserved.
 */
export async function processForUploadWithDims(
  blob: Blob,
  maxDim: number,
  quality: number,
): Promise<ProcessedImage | null> {
  if (!workerPipelineSupported) return null;
  if (!pool) pool = new WorkerPool();
  const { promise, cancel } = pool.run(blob, maxDim, quality);
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await new Promise<ProcessedImage>((resolve, reject) => {
      timer = setTimeout(() => {
        cancel(); // recycle the wedged worker + de-queue, then fall back
        reject(new Error('timeout'));
      }, JOB_TIMEOUT_MS);
      promise.then(resolve, reject);
    });
  } catch {
    // PER-FILE fallback (no permanent latch). A failed/timed-out worker job makes
    // only THIS file fall to the caller's main-thread path; the pool stays alive
    // (and at full capacity, via recycle) for every other file in the batch.
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Backward-compatible wrapper: returns only the processed JPEG Blob (or null).
 * Used by callers that don't need the source dims (HEIC pre-resize, full-res
 * fallback). Dims-aware callers should use processForUploadWithDims.
 */
export async function processForUpload(blob: Blob, maxDim: number, quality: number): Promise<Blob | null> {
  const out = await processForUploadWithDims(blob, maxDim, quality);
  return out ? out.blob : null;
}
